<template>
    <!--
        PRODUCT-26110 Task 8b Step 2: portal-vue's <portal> -> native <Teleport>. `defer`
        (Vue 3.5+) is required here: ModalTarget (the `#default-modal-target` div) is
        rendered as a LATER sibling in Main.vue's template than the <router-view> this
        Modal typically lives inside, so without `defer` the target element wouldn't
        exist yet in the DOM at the moment this Teleport resolves it.
    -->
    <Teleport v-if="open" :to="`#${target}`" defer>
        <div
            :key="uuid"
            :class="additionalClassName"
            class="Modal"
            data-test-id="Modal"
            @click="closeModalOnBackdropClick"
        >
            <component :is="form ? 'form' : 'div'" class="Modal__body" @submit="$emit('submit', $event)">
                <div v-if="$slots.above" class="Modal__above">
                    <slot name="above" />
                </div>
                <slot name="header-slot">
                    <header class="Modal__header">
                        <button v-if="allowClose" type="button" class="Modal__headerClose" @click="$emit('close')">
                            <FontAwesomeIcon :icon="closeIcon" />
                        </button>
                        <div class="Modal__headerContent">
                            <slot name="header" />
                        </div>
                    </header>
                </slot>
                <div class="Modal__content">
                    <slot />
                </div>
                <div v-if="$slots.footer" class="Modal__footer">
                    <slot name="footer" />
                </div>
            </component>
        </div>
    </Teleport>
</template>

<script lang="ts">
    import { Component, Prop, Watch, Vue } from 'vue-property-decorator';

    import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
    import { faXmark } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '../EidIcon/EidIcon.vue';

    // PRODUCT-26110 QA fixes4: declares every event this component `$emit`s -- without a
    // declared `emits`, Vue 3 warns "Component emitted event 'close'/'submit' but it is
    // neither declared in the emits option nor as an 'onClose'/'onSubmit' prop" every
    // time it fires, and (via the Teleport root) the caller's `@close`/`@submit`
    // listeners can't be auto-inherited as attrs either.
    @Component({
        components: { FontAwesomeIcon, EidIcon },
        emits: [ 'close', 'submit' ],
    })
    export default class Modal extends Vue {
        @Prop({ type: String, default: 'default-modal-target' })
        private target!: string;

        @Prop({ type: Boolean, default: true })
        private allowClose!: boolean;

        @Prop({ type: Boolean, default: false })
        private closeOnBackdrop!: boolean;

        @Prop({ type: Boolean, default: true })
        private stopClickPropagation!: boolean;

        @Prop({ type: Boolean, default: false })
        public readonly open!: boolean;

        @Prop({ type: Boolean, default: false })
        private form!: boolean;

        @Prop({ type: [ String, Array ], default: null })
        private additionalClassName!: string | string[] | null;

        @Prop({ default: () => faXmark })
        private closeIcon!: IconDefinition;

        private uuid = `${Math.random()}.${Math.random()}`;

        private closeModalOnBackdropClick(event: MouseEvent) {
            if (this.closeOnBackdrop && (event.target as HTMLElement).classList.contains('Modal')) {
                this.$emit('close');
            } else if (this.stopClickPropagation) {
                event.stopPropagation();
            }
        }

        @Watch('target', { immediate: true })
        private onTargetChange(target: string): void {
            this.$modalManager.registerModal(this, target);
        }

        @Watch('open')
        private onOpenChange(): void {
            this.$modalManager.refresh(this.target);
        }

        private unmounted() {
            this.$modalManager.unregisterModal(this);
        }
    }
</script>
